import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useConnectGongAccessKeyMutation } from '@/store/features/workspace-connections.feature';
import { apiErrorCode } from '@/lib/appsumo-upgrade';

// Where a Gong technical admin creates the key, and where the API base URL is.
const GONG_API_KEYS_URL = 'https://app.gong.io/company/api';
const GONG_BASE_URL_URL =
  'https://app.gong.io/company/api-authentication?currentTab=MY_API_TAB';

/** A pasted Gong link as its domain alone: `https://us-1.app.gong.io/home`
 * becomes `us-1.app.gong.io`. */
const gongDomain = (value: string) =>
  value
    .trim()
    .replace(/^https?:\/\//i, '')
    .split('/')[0];

const ERRORS: Record<string, string> = {
  invalid_domain:
    'That isn’t a Gong address. Paste the base URL from your Gong API page, e.g. us-12345.api.gong.io.',
  invalid_access_key:
    'Gong didn’t accept that access key and secret. Check them and try again.',
  calls_scope_denied: 'That access key can’t read calls in Gong.',
  gong_unreachable: 'Couldn’t reach Gong. Try again in a minute.',
  not_allowed:
    'Only the member who connected Gong or an org admin can replace it.',
};

/**
 * Connect Gong. OAuth (our app, approved in Gong's listing review) is the
 * default; an access key stays for companies whose admins prefer one.
 */
export function GongConnectDialog({
  organizationId,
  open,
  onOpenChange,
  onConnectOAuth,
  connectingOAuth,
}: {
  organizationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The card's OAuth start: it leaves for Gong's consent screen. */
  onConnectOAuth: () => void;
  connectingOAuth: boolean;
}) {
  const [connect, { isLoading }] = useConnectGongAccessKeyMutation();
  const [accessKey, setAccessKey] = useState('');
  const [accessKeySecret, setAccessKeySecret] = useState('');
  const [domain, setDomain] = useState('');
  const [error, setError] = useState<string | null>(null);

  const close = (next: boolean) => {
    if (!next) {
      setAccessKeySecret('');
      setError(null);
    }
    onOpenChange(next);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await connect({
        organizationId,
        accessKey: accessKey.trim(),
        accessKeySecret: accessKeySecret.trim(),
        domain: domain.trim(),
      }).unwrap();
      toast.success('Gong connected. Your calls are syncing into Knowledge.');
      setAccessKey('');
      setDomain('');
      close(false);
    } catch (err) {
      const code = apiErrorCode(err);
      setError((code && ERRORS[code]) ?? 'Couldn’t connect Gong. Try again.');
    }
  };

  const ready = accessKey.trim() && accessKeySecret.trim() && domain.trim();

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Connect Gong</DialogTitle>
          <DialogDescription>
            A Gong technical admin approves the connection or creates the access
            key.
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue='oauth'>
          <TabsList className='grid w-full grid-cols-2'>
            <TabsTrigger value='oauth'>OAuth</TabsTrigger>
            <TabsTrigger value='api_key'>API key</TabsTrigger>
          </TabsList>
          <TabsContent value='oauth' className='space-y-4 pt-2'>
            <p className='text-sm text-muted-foreground'>
              Sign in to Gong and approve read access to your team’s calls. If
              you aren’t a Gong technical admin, Gong asks one to approve it.
            </p>
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => close(false)}
              >
                Cancel
              </Button>
              <Button
                type='button'
                onClick={onConnectOAuth}
                disabled={connectingOAuth}
              >
                {connectingOAuth ? 'Opening Gong…' : 'Connect with Gong'}
              </Button>
            </DialogFooter>
          </TabsContent>
          <TabsContent value='api_key'>
            <form onSubmit={submit} className='space-y-4 pt-2'>
              <div className='space-y-1.5'>
                <Label htmlFor='gong-access-key'>Access key</Label>
                <Input
                  id='gong-access-key'
                  autoComplete='off'
                  value={accessKey}
                  onChange={(e) => setAccessKey(e.target.value)}
                />
                <p className='text-xs text-muted-foreground'>
                  Create one in{' '}
                  <a
                    href={GONG_API_KEYS_URL}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='underline underline-offset-2 hover:text-foreground'
                  >
                    Gong → Company settings → API
                  </a>
                  . Gong shows the secret only once.
                </p>
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='gong-access-key-secret'>
                  Access key secret
                </Label>
                <Input
                  id='gong-access-key-secret'
                  type='password'
                  autoComplete='off'
                  value={accessKeySecret}
                  onChange={(e) => setAccessKeySecret(e.target.value)}
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='gong-domain'>Base URL</Label>
                <Input
                  id='gong-domain'
                  placeholder='us-12345.api.gong.io'
                  autoComplete='off'
                  value={domain}
                  onChange={(e) => setDomain(gongDomain(e.target.value))}
                />
                <p className='text-xs text-muted-foreground'>
                  Find it on{' '}
                  <a
                    href={GONG_BASE_URL_URL}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='underline underline-offset-2 hover:text-foreground'
                  >
                    Gong’s API authentication page
                  </a>
                  .
                </p>
              </div>
              {error && (
                <p role='alert' className='text-sm text-destructive'>
                  {error}
                </p>
              )}
              <DialogFooter>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => close(false)}
                >
                  Cancel
                </Button>
                <Button type='submit' disabled={!ready || isLoading}>
                  {isLoading ? 'Connecting…' : 'Connect'}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
